/**
 * Locale registry — the ONE place that lists the interface languages.
 *
 * Adding a language (e.g. French):
 *   1. copy `src/i18n/locales/en/` to `src/i18n/locales/fr/` and translate the values;
 *   2. flip `enabled: true` on its entry below.
 * Nothing else changes: resources are discovered from the folder, `<html lang>`
 * and `<html dir>` follow the active language, and the LanguageSwitcher shows
 * itself as soon as more than one locale is enabled.
 *
 * Kept free of Vite/React imports so Node scripts (scripts/i18n-*.mjs) can load it.
 */

export type TextDirection = "ltr" | "rtl";

export interface LocaleInfo {
  /** BCP 47 code; also the folder name under `src/i18n/locales/`. */
  code: string;
  /** The language's own name (endonym) — shown as-is in the switcher, never translated. */
  nativeName: string;
  dir: TextDirection;
  /** Only enabled locales are offered, detected or loaded. */
  enabled: boolean;
}

export const LOCALES: readonly LocaleInfo[] = [
  { code: "en", nativeName: "English", dir: "ltr", enabled: true },
  { code: "fr", nativeName: "Français", dir: "ltr", enabled: false },
  { code: "ar", nativeName: "العربية", dir: "rtl", enabled: false },
];

export const DEFAULT_LOCALE = "en";

/** Dev-only pseudo-locale (accented + bracketed English) to spot hard-coded text. See pseudo.ts. */
export const PSEUDO_LOCALE = "en-XA";

export const SUPPORTED_LOCALES: readonly string[] = LOCALES.filter((l) => l.enabled).map((l) => l.code);

/** Translation namespaces, one JSON file each under `locales/<code>/`. Keep in sync with `types.ts`. */
export const NAMESPACES = [
  "common",
  "domain",
  "auth",
  "public",
  "layout",
  "components",
  "b2b",
  "company",
  "moderator",
  "deals",
  "kyb",
  "trade",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

/** localStorage key holding the user's explicit language choice. */
export const LANGUAGE_STORAGE_KEY = "mpi.language";

export function localeDir(code: string): TextDirection {
  const base = code.split("-")[0];
  return LOCALES.find((l) => l.code === code || l.code === base)?.dir ?? "ltr";
}
